// AI Plan Drafts Service API calls
import { createAuthenticatedApi } from './apiInterceptors';
import type { AIGoalPlan } from './aiService';

const api = createAuthenticatedApi();

export interface AIPlanDraftListItem {
  id: string;
  title: string;
  source_message: string;
  status: string;
  sub_goals_count: number;
  estimated_completion_date: string | null;
  created_at: string;
}

export interface AIPlanDraftDetail {
  id: string;
  user_id: string;
  title: string;
  plan_data: {
    title: string;
    description: string;
    estimated_completion_date: string;
    sub_goals: Array<{
      title: string;
      description: string;
      todos: Array<{
        title: string;
        description: string;
        priority: string;
      }>;
    }>;
  };
  source_message: string;
  status: string;
  converted_goal_id: string | null;
  created_at: string;
  updated_at: string;
}

export const getAIPlanDrafts = async (): Promise<AIPlanDraftListItem[]> => {
  const response = await api.get('/ai-plans');
  return response.data;
};

export const getAIPlanDraft = async (id: string): Promise<AIPlanDraftDetail> => {
  const response = await api.get(`/ai-plans/${id}`);
  return response.data;
};

export const deleteAIPlanDraft = async (id: string): Promise<void> => {
  await api.delete(`/ai-plans/${id}`);
};

export const convertAIPlanDraft = async (id: string, goalId: string): Promise<void> => {
  await api.put(`/ai-plans/${id}/convert`, { goal_id: goalId });
};

export interface ApplyAIPlanResult {
  goal: {
    id: string;
    title: string;
    description?: string;
    subGoals?: unknown[];
  };
  created: {
    subGoals: number;
    todos: number;
  };
}

/** Materialize an AI plan as Goal+SubGoals+Todos in one backend transaction. */
export const applyAIPlan = async (plan: {
  title: string;
  description?: string;
  estimated_completion_date?: string;
  sub_goals?: AIPlanDraftDetail['plan_data']['sub_goals'];
  draft_id?: string;
}): Promise<ApplyAIPlanResult> => {
  const response = await api.post('/goals/from-ai-plan', plan);
  return response.data;
};

/** Write one sub-goal (and its todos) anew; the other sub-goals stay. Returns the whole plan (also saved in the draft). */
export const regenerateSubGoal = async (
  draftId: string, plan: AIGoalPlan, index: number, feedback?: string
): Promise<AIGoalPlan> => {
  const response = await api.post(`/ai-plans/${draftId}/regenerate-sub-goal`, { plan, index, feedback });
  return response.data;
};

/** Revise the plan by a change request ("shorter", "more practice"); what it does not touch stays. */
export const revisePlan = async (draftId: string, plan: AIGoalPlan, feedback: string): Promise<AIGoalPlan> => {
  const response = await api.post(`/ai-plans/${draftId}/revise`, { plan, feedback });
  return response.data;
};
